import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createProduct,
  deleteProduct,
  fetchProductSuggestions,
  fetchSalonProducts,
  publishProduct,
  updateProduct,
} from '../../api/products';
import { fetchBranches } from '../../api/salon';
import ProductFormPanel from '../../components/products/ProductFormPanel';
import DashboardShell from '../../components/ui/DashboardShell';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import EmptyState from '../../components/ui/EmptyState';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { ListSkeleton } from '../../components/ui/Skeleton';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import ListSearchToolbar from '../../components/ui/ListSearchToolbar';
import { useListSearch } from '../../hooks/useListSearch';
import { PRODUCT_CATEGORIES } from '../../constants/productCategories';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const SalonProductsPage = () => {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [category, setCategory] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {
    input: searchInput,
    setInput: setSearchInput,
    appliedSearch,
    commitSearch,
    suggestions,
    suggestionsLoading,
  } = useListSearch({
    queryKeyPrefix: ['product-suggestions', tenantId, category, branchFilter],
    fetchSuggestions: (q) =>
      fetchProductSuggestions({
        q,
        category: category || undefined,
        branchId: branchFilter || undefined,
      }),
    enabled: !!tenantId,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['salon-products', tenantId, page, pageSize, category, branchFilter, appliedSearch],
    queryFn: () =>
      fetchSalonProducts({
        page,
        limit: pageSize,
        category: category || undefined,
        branchId: branchFilter || undefined,
        search: appliedSearch || undefined,
      }),
    enabled: !!tenantId,
  });

  const { data: branchData } = useQuery({
    queryKey: ['branches'],
    queryFn: fetchBranches,
  });

  const products = data?.products || [];
  const pagination = data?.pagination;
  const branches = branchData?.branches || [];

  const saveMut = useMutation({
    mutationFn: (payload) =>
      editing
        ? updateProduct(editing._id, {
            name: payload.name,
            description: payload.description,
            category: payload.category,
            price: Number(payload.price),
            stock: Number(payload.stock),
            lowStockThreshold: Number(payload.lowStockThreshold),
            branchId: payload.branchId,
            paymentSlipPolicy: payload.paymentSlipPolicy,
            isPublished: payload.isPublished,
          })
        : createProduct(
            {
              name: payload.name,
              description: payload.description,
              category: payload.category,
              price: payload.price,
              stock: payload.stock,
              lowStockThreshold: payload.lowStockThreshold,
              branchId: payload.branchId || '',
              paymentSlipPolicy: payload.paymentSlipPolicy,
              isPublished: payload.isPublished,
            },
            payload.files,
            (e) => {
              if (e.total) payload.onProgress({ percent: Math.round((e.loaded / e.total) * 100) });
            }
          ),
    onSuccess: () => {
      showToast(editing ? t('toast.productUpdated') : t('toast.productCreated'), 'success');
      setPanelOpen(false);
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['salon-products'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.errorGeneric'), 'error'),
  });

  const publishMut = useMutation({
    mutationFn: ({ id, isPublished }) => publishProduct(id, isPublished),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['salon-products'] }),
  });

  const deleteMut = useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => {
      showToast(t('toast.productRemoved'), 'success');
      queryClient.invalidateQueries({ queryKey: ['salon-products'] });
    },
  });

  if (!tenantId) {
    return (
      <DashboardShell title={t('salon.products.title')} subtitle={t('salon.products.catalogSubtitle')}>
        <EmptyState
          icon="🏪"
          title={t('salon.products.tenantRequiredTitle')}
          subtitle={t('salon.products.tenantRequiredSubtitle')}
        />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell title={t('salon.products.title')} subtitle={t('salon.products.catalogSubtitle')}>
      <ListSearchToolbar
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearch={(v) => { commitSearch(v); setPage(1); }}
        suggestions={suggestions}
        suggestionsLoading={suggestionsLoading}
        searchPlaceholder={t('salon.products.searchPlaceholder')}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((o) => !o)}
      >
        <select
          value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(1); }}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
        >
          <option value="">{t('common.all')}</option>
          {PRODUCT_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={branchFilter}
          onChange={(e) => { setBranchFilter(e.target.value); setPage(1); }}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
        >
          <option value="">{t('salon.products.allBranches')}</option>
          {branches.map((b) => (
            <option key={b._id} value={b._id}>{b.name}</option>
          ))}
        </select>
      </ListSearchToolbar>

      <div className="mb-6 flex justify-end">
        <button
          type="button"
          onClick={() => { setEditing(null); setPanelOpen(true); }}
          className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white"
        >
          {t('salon.products.addProductButton')}
        </button>
      </div>

      {isLoading && <ListSkeleton rows={4} />}

      {!isLoading && products.length === 0 && (
        <EmptyState
          icon="🛍️"
          title={t('salon.products.addFirstTitle')}
          subtitle={t('salon.products.addFirstDesc')}
          action={
            <button
              type="button"
              onClick={() => { setEditing(null); setPanelOpen(true); }}
              className="rounded-lg bg-glow-600 px-4 py-2 text-sm font-medium text-white"
            >
              {t('salon.products.addProduct')}
            </button>
          }
        />
      )}

      {!isLoading && products.length > 0 && (
      <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 text-zinc-600 dark:text-zinc-500">
            <tr>
              <th className="p-3">{t('salon.products.imageColumn')}</th>
              <th className="p-3">{t('common.name')}</th>
              <th className="p-3">{t('salon.products.category')}</th>
              <th className="p-3">{t('salon.products.stock')}</th>
              <th className="p-3">{t('common.price')}</th>
              <th className="p-3">{t('salon.products.published')}</th>
              <th className="p-3">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const low = p.stock <= p.lowStockThreshold && p.stock > 0;
              const out = p.stock <= 0;
              return (
                <tr key={p._id} className="border-b border-zinc-200/50 dark:border-zinc-800/50">
                  <td className="p-3">
                    {p.images?.[0]?.url ? (
                      <img src={p.images[0].url} alt="" loading="lazy" className="h-10 w-10 rounded object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-zinc-100 dark:bg-zinc-800" />
                    )}
                  </td>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-zinc-600 dark:text-zinc-500">{p.category}</td>
                  <td className="p-3">
                    {p.stock}
                    {low && <span className="ml-1 text-amber-400" title={t('salon.products.lowStockTitle')}>⚠</span>}
                    {out && <span className="ml-1 text-red-400">{t('salon.products.outLabel')}</span>}
                  </td>
                  <td className="p-3">{t('common.currency', { amount: p.price?.toLocaleString() })}</td>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={p.isPublished}
                      onChange={() => publishMut.mutate({ id: p._id, isPublished: !p.isPublished })}
                    />
                  </td>
                  <td className="p-3">
                    <button type="button" className="text-glow-400 hover:underline" onClick={() => { setEditing(p); setPanelOpen(true); }}>
                      {t('common.edit')}
                    </button>
                    <button type="button" className="ml-3 text-red-400 hover:underline" onClick={() => setConfirmDelete(p)}>
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <PaginatedFooter
          pagination={pagination}
          page={page}
          onPageChange={setPage}
          pageSize={pageSize}
          onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        />
      </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title={t('salon.products.deleteConfirm')}
        message={t('salon.products.deleteMessage', { name: confirmDelete?.name })}
        confirmLabel={t('common.delete')}
        confirmVariant="danger"
        loading={deleteMut.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          deleteMut.mutate(confirmDelete._id, { onSettled: () => setConfirmDelete(null) });
        }}
      />

      <ProductFormPanel
        open={panelOpen}
        onClose={() => { setPanelOpen(false); setEditing(null); }}
        product={editing}
        branches={branches}
        saving={saveMut.isPending}
        onSave={(fields) => saveMut.mutate(fields)}
      />
    </DashboardShell>
  );
};

export default SalonProductsPage;
